import * as Collapsible from '@radix-ui/react-collapsible'
import { Box, Button, Flex, Text } from '@radix-ui/themes'
import parse from 'html-react-parser'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiCollapse = 'ui-collapse',
}

/* -------------------------------------------------------------------------------------------------
 * ProductCollapse
 * -----------------------------------------------------------------------------------------------*/

type ProductCollapseProps = {
	description: string
	max?: number
}

const ProductCollapseContext = React.createContext<{
	collapseHighlight?: string
	setCollapseHighlight?: React.Dispatch<React.SetStateAction<string>>
	collapseOpen?: boolean
	setCollapseOpen?: React.Dispatch<React.SetStateAction<boolean>>
}>({})

const ProductCollapse: React.FC<ProductCollapseProps> = (props) => {
	const { description, max = 400 } = props

	const {
		collapseHighlight,
		setCollapseHighlight,
		collapseOpen,
		setCollapseOpen,
	} = React.useContext(ProductCollapseContext)
	const show = React.useMemo(() => description.length > max, [description, max])
	const { t } = useTranslation(I18NEXT.uiCollapse)

	const ref = React.useRef<HTMLParagraphElement>(null)
	const [slow, setSlow] = React.useState<boolean>(true)

	const parsedClosed = React.useMemo(
		() => parse(description?.slice(0, max) + '...' || ''),
		[description, max],
	)
	const parsedOpened = React.useMemo(
		() => parse(description) ?? '',
		[description],
	)

	React.useEffect(() => {
		setCollapseOpen!(!show)
	}, [show, setCollapseOpen])

	React.useEffect(() => {
		let timeout: ReturnType<typeof setTimeout> | undefined = undefined
		const nodes = ref.current?.querySelectorAll('span[data-info]')
		nodes?.forEach((node) => {
			node.classList.remove('highlight')
			if (node.getAttribute('data-info') === collapseHighlight) {
				;(async () => {
					node.classList.add('highlight')
					await new Promise((_) => (timeout = setTimeout(_, slow ? 1500 : 0)))
					setSlow(false)
					node.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					})
				})()
			}
		})
		return () => {
			if (timeout) {
				clearTimeout(timeout)
			}
		}
	}, [collapseHighlight, ref, slow, setSlow])

	const onOpenChangeHandler = (open: boolean) => {
		setCollapseOpen!(open)
		setSlow(true)
	}

	return (
		<Collapsible.Root
			asChild
			open={collapseOpen}
			onOpenChange={onOpenChangeHandler}
			className="app-Collapsible"
		>
			<Flex direction="column" gapY="3">
				{!collapseOpen && show && (
					<Text
						asChild
						color="gray"
						align={{
							initial: 'center',
							lg: 'left',
						}}
						className="whitespace-pre-line"
					>
						<Box>{parsedClosed}</Box>
					</Text>
				)}
				<Collapsible.Content className="app-Collapse">
					<Text
						ref={ref}
						asChild
						color="gray"
						align={{
							initial: 'center',
							lg: 'left',
						}}
						className="whitespace-pre-line"
					>
						<Box>{parsedOpened}</Box>
					</Text>
				</Collapsible.Content>
				{show && (
					<Collapsible.Trigger asChild>
						<Flex justify="center" width="100%">
							<Button
								color="green"
								size="1"
								variant="soft"
								onClick={() => setCollapseHighlight!('')}
							>
								{collapseOpen ? t('button.less') : t('button.more')}
							</Button>
						</Flex>
					</Collapsible.Trigger>
				)}
			</Flex>
		</Collapsible.Root>
	)
}
ProductCollapse.displayName = 'ProductCollapse'

/* -----------------------------------------------------------------------------------------------*/

export {
	ProductCollapse,
	ProductCollapseContext,
	I18NEXT as PRODUCT_COLLAPSE_I18NEXT,
}
